/**
 * Move generation.
 *
 * `pseudoLegalMoves` yields all moves that obey each piece's movement pattern,
 * ignoring whether the mover leaves their own king in check or facing the enemy
 * king. `legalMoves` filters those by actually making the move and rejecting any
 * that leave the mover in check or the kings facing (the flying-general rule).
 */

import { inCheck, isSquareAttacked, kingsFacing } from "./attacks.js";
import { Position } from "./position.js";
import {
  Color,
  PieceType,
  fileOf,
  inPalace,
  isOnBoard,
  ownSide,
  pawnHasCrossed,
  rankOf,
  square,
  type Move,
  type Piece,
  type Square,
} from "./types.js";

const ORTHO: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
const DIAG: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];
const HORSE: ReadonlyArray<readonly [number, number]> = [
  [1, 2],
  [-1, 2],
  [1, -2],
  [-1, -2],
  [2, 1],
  [-2, 1],
  [2, -1],
  [-2, -1],
];

function pushIfTarget(
  pos: Position,
  from: Square,
  f: number,
  r: number,
  mover: Color,
  out: Move[],
): void {
  if (!isOnBoard(f, r)) return;
  const dest = pos.board[square(f, r)];
  if (!dest || dest.color !== mover) out.push({ from, to: square(f, r) });
}

function genPiece(pos: Position, from: Square, piece: Piece, out: Move[]): void {
  const ff = fileOf(from);
  const fr = rankOf(from);
  const me = piece.color;

  switch (piece.type) {
    case PieceType.King: {
      for (const [df, dr] of ORTHO) {
        const tf = ff + df;
        const tr = fr + dr;
        if (isOnBoard(tf, tr) && inPalace(square(tf, tr), me)) {
          pushIfTarget(pos, from, tf, tr, me, out);
        }
      }
      break;
    }
    case PieceType.Advisor: {
      for (const [df, dr] of DIAG) {
        const tf = ff + df;
        const tr = fr + dr;
        if (isOnBoard(tf, tr) && inPalace(square(tf, tr), me)) {
          pushIfTarget(pos, from, tf, tr, me, out);
        }
      }
      break;
    }
    case PieceType.Elephant: {
      for (const [df, dr] of DIAG) {
        const tf = ff + df * 2;
        const tr = fr + dr * 2;
        if (!isOnBoard(tf, tr)) continue;
        if (!ownSide(square(tf, tr), me)) continue; // cannot cross the river
        if (pos.board[square(ff + df, fr + dr)]) continue; // 塞象眼: midpoint blocked
        pushIfTarget(pos, from, tf, tr, me, out);
      }
      break;
    }
    case PieceType.Horse: {
      for (const [df, dr] of HORSE) {
        const tf = ff + df;
        const tr = fr + dr;
        if (!isOnBoard(tf, tr)) continue;
        const legf = ff + (Math.abs(df) === 2 ? Math.sign(df) : 0);
        const legr = fr + (Math.abs(dr) === 2 ? Math.sign(dr) : 0);
        if (pos.board[square(legf, legr)]) continue; // 蹩馬腿: leg blocked
        pushIfTarget(pos, from, tf, tr, me, out);
      }
      break;
    }
    case PieceType.Rook: {
      for (const [df, dr] of ORTHO) {
        let tf = ff + df;
        let tr = fr + dr;
        while (isOnBoard(tf, tr)) {
          const dest = pos.board[square(tf, tr)];
          if (!dest) {
            out.push({ from, to: square(tf, tr) });
          } else {
            if (dest.color !== me) out.push({ from, to: square(tf, tr) });
            break;
          }
          tf += df;
          tr += dr;
        }
      }
      break;
    }
    case PieceType.Cannon: {
      for (const [df, dr] of ORTHO) {
        let tf = ff + df;
        let tr = fr + dr;
        // Non-capturing slides over empty squares.
        while (isOnBoard(tf, tr) && !pos.board[square(tf, tr)]) {
          out.push({ from, to: square(tf, tr) });
          tf += df;
          tr += dr;
        }
        // Reached a screen; jump it and look for the first piece beyond.
        tf += df;
        tr += dr;
        while (isOnBoard(tf, tr) && !pos.board[square(tf, tr)]) {
          tf += df;
          tr += dr;
        }
        if (isOnBoard(tf, tr)) {
          const dest = pos.board[square(tf, tr)]!;
          if (dest.color !== me) out.push({ from, to: square(tf, tr) });
        }
      }
      break;
    }
    case PieceType.Pawn: {
      const fwd = me === Color.Red ? 1 : -1;
      // Forward one.
      if (isOnBoard(ff, fr + fwd)) pushIfTarget(pos, from, ff, fr + fwd, me, out);
      // Sideways one, only after crossing the river.
      if (pawnHasCrossed(from, me)) {
        pushIfTarget(pos, from, ff - 1, fr, me, out);
        pushIfTarget(pos, from, ff + 1, fr, me, out);
      }
      break;
    }
  }
}

export function pseudoLegalMoves(pos: Position, color: Color = pos.sideToMove): Move[] {
  const out: Move[] = [];
  for (let sq = 0; sq < pos.board.length; sq++) {
    const p = pos.board[sq];
    if (p && p.color === color) genPiece(pos, sq, p, out);
  }
  return out;
}

/** True if making `move` would leave `mover` in check or the two kings facing. */
export function leavesKingExposed(pos: Position, move: Move, mover: Color): boolean {
  const next = pos.clone();
  next.makeMove(move);
  return inCheck(next, mover) || kingsFacing(next);
}

export function legalMoves(pos: Position, color: Color = pos.sideToMove): Move[] {
  return pseudoLegalMoves(pos, color).filter((m) => !leavesKingExposed(pos, m, color));
}

export function isLegalMove(pos: Position, move: Move): boolean {
  const p = pos.board[move.from];
  if (!p || p.color !== pos.sideToMove) return false;
  const pseudo = pseudoLegalMoves(pos, pos.sideToMove);
  if (!pseudo.some((m) => m.from === move.from && m.to === move.to)) return false;
  return !leavesKingExposed(pos, move, pos.sideToMove);
}

// Re-export for convenience at the rules-core level.
export { inCheck, isSquareAttacked, kingsFacing };
